import React, { useEffect, useRef, useState } from 'react';

import GoogleMapReact, { fitBounds } from 'google-map-react';
import styled from 'styled-components';

import { usePublicationContext } from 'components/Publication/PublicationContext';
import { DEFAULT_MAP_PADDING_METERS } from 'constants/map';
import { useAppSelector } from 'store/hook';
import { CommunityType } from 'types/communities';
import { metersToLatDegrees, metersToLngDegrees } from 'utils/helpers/location';

import { GOOGLE_MAP_HEIGHT, GOOGLE_MAP_ZOOM, USA_CENTER_COORDINATES } from './constants';
import MapInfoPanel from './MapInfoPanel';
import MapMarker from './MapMarker';

const Container = styled.div`
  display: flex;
  position: relative;
  width: 100%;
  height: 100%;
  a[href^='http://maps.google.com/maps'] {
    display: none !important;
  }

  .gmnoprint a,
  .gmnoprint span {
    display: none;
  }
  .gmnoprint div {
    background: none !important;
  }
  .gmnoprint,
  .gm-style-mtc {
    margin-bottom: 20px;
  }
  .gm-style button {
    margin-bottom: 20px;
  }
`;

type Props = {
  communities: CommunityType[];
  competitors: CommunityType[];
};

const hasValidCoordinates = (community: CommunityType) =>
  !isNaN(parseFloat(community.latitude ?? '')) && !isNaN(parseFloat(community.longitude ?? ''));

const Map = ({ communities, competitors }: Props) => {
  const { selectedCommunity } = useAppSelector((state) => state.global);
  const { publication } = usePublicationContext();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const showInfoPanel = Boolean(selectedCommunity || publication?.hash);

  const [googleMapsReady, setGoogleMapsReady] = useState<boolean>(false);
  const [mapCenter, setMapCenter] = useState<any>(USA_CENTER_COORDINATES);
  const [mapHeight, setMapHeight] = useState<any>(GOOGLE_MAP_HEIGHT);
  const [zoom, setZoom] = useState<any>(GOOGLE_MAP_ZOOM);
  const [bounds, setBounds] = useState<any>(null);

  useEffect(() => {
    const allCommunities = [...(communities ?? []), ...(competitors ?? [])];

    if (!allCommunities.length || !googleMapsReady) {
      return;
    }

    const allCommunitiesValidCoordinates = allCommunities.filter(hasValidCoordinates);

    if (!allCommunitiesValidCoordinates.length) {
      setBounds(null);
      setMapCenter(USA_CENTER_COORDINATES);
      setZoom(GOOGLE_MAP_ZOOM);
      return;
    }

    if (allCommunitiesValidCoordinates.length === 1) {
      const { latitude, longitude } = allCommunitiesValidCoordinates[0];
      const lat = Number(latitude);
      const lng = Number(longitude);

      // Add ~400m padding so bounds aren't a single point
      const dLat = metersToLatDegrees(DEFAULT_MAP_PADDING_METERS);
      const dLng = metersToLngDegrees(DEFAULT_MAP_PADDING_METERS, lat);

      setBounds({
        ne: { lat: lat + dLat, lng: lng + dLng },
        sw: { lat: lat - dLat, lng: lng - dLng },
      });
      return;
    }

    const mapBounds = new google.maps.LatLngBounds();
    allCommunitiesValidCoordinates.forEach((marker: CommunityType) => {
      mapBounds.extend(new google.maps.LatLng(Number(marker?.latitude), Number(marker?.longitude)));
    });

    const mapBoxBounds = {
      ne: {
        lat: mapBounds.getNorthEast()?.lat(),
        lng: mapBounds.getNorthEast()?.lng(),
      },
      sw: {
        lat: mapBounds.getSouthWest()?.lat(),
        lng: mapBounds.getSouthWest()?.lng(),
      },
    };

    if (typeof mapBoxBounds.ne.lat !== 'number' || typeof mapBoxBounds.ne.lng !== 'number') {
      setBounds(null);
      return;
    }
    if (typeof mapBoxBounds.sw.lat !== 'number' || typeof mapBoxBounds.sw.lng !== 'number') {
      setBounds(null);
      return;
    }

    setBounds(mapBoxBounds);
  }, [communities, competitors, googleMapsReady]);

  useEffect(() => {
    if (bounds && mapContainerRef.current) {
      const containerWidth = mapContainerRef.current.clientWidth;
      const containerHeight = mapContainerRef.current.clientHeight;

      const { center, zoom } = fitBounds(bounds, {
        width: containerWidth,
        height: containerHeight,
      });

      setMapCenter(center);
      setZoom(zoom);
      setMapHeight(containerHeight);
    }
  }, [bounds, mapContainerRef.current?.clientWidth, mapContainerRef.current?.clientHeight]);

  return (
    <Container ref={mapContainerRef}>
      <GoogleMapReact
        bootstrapURLKeys={{
          key: 'AIzaSyAkEDUPfo1e_5N59IClF74jjicyKbTMYw4',
          libraries: ['places'],
        }}
        center={mapCenter}
        zoom={zoom}
        options={{
          zoomControl: true,
          fullscreenControl: false,
          keyboardShortcuts: false,
        }}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={() => setGoogleMapsReady(true)}
        style={{
          height: mapHeight,
        }}
      >
        {[...(communities ?? []), ...(competitors ?? [])]
          ?.filter((community: CommunityType) => hasValidCoordinates(community))
          .map((data: any) => (
            <MapMarker key={data?.id} lat={data?.latitude} lng={data?.longitude} data={data} />
          ))}
      </GoogleMapReact>
      {showInfoPanel && <MapInfoPanel communities={communities} competitors={competitors} />}
    </Container>
  );
};

export default Map;
